import { ReactNode, useEffect, useRef, useState } from "react";
import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { DropdownSelector } from "./DropdownSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "../ClassName";
import useClickOutside from "../useClickOutside";
import useKeyPressOnElement from "../hooks/useKeyPressOnElement";

interface Props<Type> {
  //Props identical to DropdownSelector
  selectedOption: any;
  dropdownItems: Type[];
  onClick: (i: Type) => void;
  getKey: (item: Type) => string | number | null;
  getItemChildren: (item: Type) => ReactNode;
  dropdownTitle?: string;
  dropdownSelectorClassName?: string;

  /**
   * @type string
   * @description A Tailwind CSS string indicating the background color.
   */
  textColor?: string;
  /**
   * @type string
   * @description A Tailwind CSS string indicating the background color.
   */
  backgroundColor?: string;
  /**
   * @type string
   * @description Placeholder string for the div when no option is selected.
   */
  placeholder: string;
  /**
   * @type any
   * @description When clearing out the selection we need to this emptyOption to trigger the onClick and clear the parent state.
   */
  emptyOption?: any;
  /**
   * @type (option: any) => string
   * @description A function that given an option returns a string to be displayed in the dive
   */
  getDivDisplay: (item: Type) => string;
  /**
   * @type string
   * @description A string indicating if the field has an active warning or error
   */
  hasActiveWarning?: string;
  /**
   * @type () => void
   * @description A function that triggers validations for the line item.
   */
  isEditable?: boolean;
  validateSelections?: () => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  buttonOnClick?: () => void;
  dataTestId?: string;
  setFieldClearedIndex?: () => void;
  children?: ReactNode;
}

export default function DivWithDropdownSelector<Type>(props: Props<Type>) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [divDisplay, setDivDisplay] = useState<string>("");
  const inputRef = useRef<HTMLDivElement>(null);
  const enterPressed = useKeyPressOnElement("Enter", inputRef.current);

  useEffect(() => {
    setDivDisplay(props.getDivDisplay(props.selectedOption));
    // Since we allow a clear selection option we run validation whenever state updates.

    props.validateSelections?.();
  }, [props.selectedOption]);

  useClickOutside(inputRef, () => setExpanded(false));

  useEffect(() => {
    if (enterPressed) {
      if (document.activeElement === inputRef.current) {
        if (!expanded) {
          setExpanded(true);
        }
      }
    }
  }, [enterPressed]);

  return (
    <div ref={inputRef} tabIndex={0}>
      <div
        className={cn(
          `tw-group tw-flex tw-h-[36px] tw-w-full tw-min-w-fit tw-cursor-pointer tw-items-center tw-text-nowrap tw-rounded-lg tw-border-solid`,
          expanded
            ? `tw-border-[2px] !tw-border-blue-2 tw-p-[7px] tw-shadow-[0px_0px_4px_0px_rgba(0,133,222,0.50)] tw-outline-0`
            : "tw-border-[1px] tw-border-gray-5 tw-p-[8px] hover:tw-border-gray-0",
          props.backgroundColor
            ? props.backgroundColor
            : props.hasActiveWarning
              ? props.hasActiveWarning === "Error"
                ? "tw-border-red-1 tw-bg-red-light-1"
                : "tw-border-orange-1 tw-bg-orange-light-1"
              : "tw-bg-white",
          props.className,
        )}
        onClick={(e) => {
          if (props.buttonOnClick !== undefined) {
            props.buttonOnClick();
          } else {
            setExpanded(!expanded);
          }
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <input
          tabIndex={-1}
          className={`tw-w-fit tw-flex-grow tw-border-none tw-bg-transparent tw-px-0 tw-text-left tw-outline-none placeholder:tw-text-gray-5 ${props.textColor ? props.textColor : divDisplay === "" && !props.isEditable ? "tw-text-gray-5" : "tw-text-black"} ${props.inputClassName}`}
          type={props.isEditable ? "text" : "button"}
          value={
            props.isEditable ? divDisplay : divDisplay || props.placeholder
          }
          placeholder={props.placeholder}
          data-testid={props.dataTestId}
          onChange={(e) => props.onClick(e.target.value as Type)}
          disabled={props.disabled}
        />
        <div className="tw-float-right tw-flex-initial">
          {divDisplay && props.emptyOption !== undefined ? (
            <div
              className={`tw-flex tw-size-[16px] tw-cursor-pointer tw-items-center tw-justify-center tw-bg-transparent`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (props.setFieldClearedIndex) {
                  props.setFieldClearedIndex();
                }

                props.onClick(props.emptyOption);
                setDivDisplay("");
                setExpanded(false);
              }}
            >
              <FontAwesomeIcon
                className="tw-bg-transparent tw-text-[14px] tw-text-gray-4"
                icon={faXmark}
              />
            </div>
          ) : (
            <div
              className="tw-flex tw-size-[16px] tw-items-center tw-justify-center"
              onClick={(e) => {
                if (props.buttonOnClick !== undefined) {
                  props.buttonOnClick();
                } else {
                  setExpanded(!expanded);
                }
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FontAwesomeIcon
                className={`tw-self-center tw-bg-transparent tw-text-[14px] tw-text-gray-4`}
                icon={expanded ? faChevronUp : faChevronDown}
              />
            </div>
          )}
        </div>
      </div>
      {expanded && !props.children ? (
        <DropdownSelector
          className={props.dropdownSelectorClassName}
          dropdownItems={props.dropdownItems}
          getKey={props.getKey}
          getItemChildren={props.getItemChildren}
          onClick={(i) => {
            props.onClick(i);
            setDivDisplay(props.getDivDisplay(i));
            setExpanded(false);
          }}
          selectedOption={props.selectedOption}
          title={props.dropdownTitle}
          toggleDropdown={() => setExpanded(!expanded)}
          dropDownListElement={inputRef.current}
        />
      ) : expanded && props.children ? (
        props.children
      ) : null}
    </div>
  );
}
